import { api, acct } from "./client";
import { crud, type Doc } from "./crud";
import type { ListQuery } from "./types";

/**
 * Call queues. Hand-typed from `applications/crossbar/priv/couchdb/schemas/
 * queues.json` and `applications/crossbar/doc/queues.md`.
 *
 * Two things are unlike the other PBX collections:
 *
 * - a queue's `agents` list is edited through the `agents` sub-resource,
 *   not by writing the document, so a supervisor can move one agent
 *   without racing whoever else has the queue open;
 * - `status` is not a document. It is a live query against the node
 *   dispatching the account, so it is never cached and it is the first
 *   paint under the `callcenter.*` events rather than the source of truth.
 */

/** How the queue picks which agent to offer a caller to. */
export type QueueStrategy = "ring_all" | "longest_idle" | "round_robin" | "least_talk_time" | "fewest_calls" | "sequential" | "most_idle";

/**
 * The strategies in the order the queue form offers them. `most_idle` is
 * the old spelling of `longest_idle` and is not offered: it is kept only
 * so documents written before the rename still validate, and the form
 * shows it when a document carries it.
 */
export const QUEUE_STRATEGIES: { value: QueueStrategy; label: string; help: string; implemented: boolean }[] = [
  { value: "ring_all", label: "Ring everyone", help: "Rings every eligible agent at once; the first to answer takes the caller.", implemented: true },
  { value: "longest_idle", label: "Longest idle", help: "The agent who has been idle longest since their last queue call.", implemented: true },
  { value: "round_robin", label: "Round robin", help: "Rotates through the agents in tier order, remembering who was tried last.", implemented: true },
  { value: "least_talk_time", label: "Least talk time", help: "The agent with the least time on queue calls since they logged in.", implemented: true },
  { value: "fewest_calls", label: "Fewest calls", help: "The agent who has answered the fewest queue calls since they logged in.", implemented: true },
  { value: "sequential", label: "In order", help: "Starts at the top of the tier order for every caller; the agents below take the overflow.", implemented: true },
];

/** The alias the schema still accepts, named for the one document that carries it. */
export const STRATEGY_ALIAS: QueueStrategy = "most_idle";

export const strategyLabel = (s: string | undefined): string =>
  s === STRATEGY_ALIAS ? "Longest idle (old spelling)" : (QUEUE_STRATEGIES.find((x) => x.value === s)?.label ?? "Longest idle");

/** The availability an agent is moved to once they have missed `max_no_answer` offers. */
export type Availability = "logged_out" | "available" | "available_on_demand" | "on_break";

/** One agent's tier in a queue, as the document stores it. */
export interface QueueTier {
  id: string;
  level?: number;
  position?: number;
}

export interface QueueSummary extends Doc {
  id: string;
  name?: string;
}

export interface QueueAnnounce {
  media?: string;
  interval?: number;
  position?: boolean;
}

export interface QueueTierRules {
  apply?: boolean;
  wait_seconds?: number;
  wait_multiply_level?: boolean;
  no_agent_no_wait?: boolean;
}

export interface QueueDoc extends QueueSummary {
  name?: string;
  strategy?: QueueStrategy;
  agents?: QueueTier[];
  /** Media played once as the caller enters. */
  greeting?: string;
  /** Music on hold; `silence_stream://300000` for silence. */
  moh?: string;
  announce?: QueueAnnounce;
  /** DTMF keys that leave the queue and continue the callflow. */
  exit_keys?: string;
  /** Deprecated single-key form of `exit_keys`; still read, never written by this console. */
  caller_exit_key?: string;
  max_wait_time?: number;
  max_wait_time_with_no_agent?: number;
  max_wait_time_with_no_agent_time_reached?: number;
  enter_when_empty?: boolean;
  max_queue_size?: number;
  tier_rules?: QueueTierRules;
  time_base_score?: "system" | "queue";
  agent_ring_timeout?: number;
  agent_wrapup_time?: number;
  agent_no_answer_status?: Availability;
  ring_simultaneously?: number;
  skip_agents_with_external_calls?: boolean;
  record?: boolean;
  cid_prefix?: string;
  outbound_caller_id?: { name?: string; number?: string };
  /**
   * The prompts spoken around the caller's position when `announce.position`
   * is on. The console keeps them and does not offer them yet. Keys an older
   * document carries beside `media` are no longer in the schema.
   */
  announcements?: { media?: { you_are_at_position?: string; in_the_queue?: string }; [key: string]: unknown };
  ui_metadata?: { ui?: string; version?: string; origin?: string };
  [key: string]: unknown;
}

/** A row of `GET .../queues/{id}/agents`: the tier with the user summarised. */
export interface QueueTierRow {
  id: string;
  level?: number;
  position?: number;
  first_name?: string;
  last_name?: string;
  username?: string;
}

/** The agent's state in one queue, from `status_resp`. */
export type TierState = "ready" | "offering" | "active_inbound" | "standby" | "no_answer";
/** What the agent's phones are doing; derived, never chosen. */
export type Activity = "idle" | "ringing" | "on_call" | "wrapping_up";

export interface QueueStatusAgent {
  agent_id: string;
  availability?: Availability;
  activity?: Activity;
  tier_level?: number;
  tier_position?: number;
  tier_state?: TierState;
  /** Gregorian seconds. */
  last_call_end?: number;
  answered?: number;
  missed?: number;
  /**
   * The caller the agent is being rung for or is talking to, on a queue
   * call; `null` when there is none. Listening in on it hears both of them.
   * A backend of the release before sends no key at all.
   */
  call_id?: string | null;
  /** The queue that caller came in through: another of the agent's queues when they are in more than one. */
  queue_id?: string | null;
  /** True once the agent answered `call_id`; false while their phones ring. */
  connected?: boolean | null;
}

export interface QueueStatusCaller {
  call_id: string;
  position?: number;
  /** Gregorian seconds. */
  joined_timestamp?: number;
  caller_id_number?: string;
  caller_id_name?: string;
  /** The media server the caller's channel is parked on. */
  media_node?: string;
  /** The agent currently being tried, when there is one. */
  offered_to?: string | null;
}

export interface QueueStatus {
  queue_id?: string;
  waiting?: QueueStatusCaller[];
  agents?: QueueStatusAgent[];
}

/** Whose phone the supervisor action calls: their device, or the user's endpoints. */
export type SupervisorTarget = { device_id: string; user_id?: never } | { user_id: string; device_id?: never };

const base = crud<QueueSummary, QueueDoc>("queues");

export const queues = {
  ...base,
  /** The tier sub-resource: the queue's `agents` list, each with the user summarised. */
  agents: {
    list: (accountId: string, queueId: string, q?: ListQuery) => api.list<QueueTierRow>(acct(accountId, "queues", queueId, "agents"), { paginate: false, ...q }),
    /** `level` and `position` both default to 1. A user who already has a tier is an error; change theirs instead. */
    add: (accountId: string, queueId: string, tier: QueueTier) => api.put<QueueTierRow>(acct(accountId, "queues", queueId, "agents"), tier),
    change: (accountId: string, queueId: string, userId: string, tier: Partial<Omit<QueueTier, "id">>) =>
      api.patch<QueueTierRow>(acct(accountId, "queues", queueId, "agents", userId), tier),
    remove: (accountId: string, queueId: string, userId: string) => api.delete(acct(accountId, "queues", queueId, "agents", userId)),
  },
  /** Who is waiting and who could take them, as of now. Never cached. */
  status: (accountId: string, queueId: string) => api.get<QueueStatus>(acct(accountId, "queues", queueId, "status")),
  /** Calls the supervisor and puts them on an agent's call, listening only. */
  eavesdrop: (accountId: string, queueId: string, body: SupervisorTarget & { call_id: string }) => api.put(acct(accountId, "queues", queueId, "eavesdrop"), body),
  /**
   * Rings the supervisor's phones for a caller waiting in the queue. A 202
   * means the phones are ringing, not that anybody has the caller: the
   * caller leaves the queue only when the supervisor answers, and stays
   * waiting in the same place when nobody does. A refusal's envelope
   * `message` names it (`PickupRefusal`).
   */
  pickup: (accountId: string, queueId: string, body: SupervisorTarget & { call_id: string }) => api.put<PickupRinging>(acct(accountId, "queues", queueId, "pickup"), body),
};

/** What a `202` from `pickup` says is ringing. */
export interface PickupRinging {
  call_id?: string;
  queue_id?: string;
  device_id?: string;
  user_id?: string;
  /** The device ids ringing. */
  ringing?: string[];
  /** Seconds the phones ring before the caller goes back to waiting for an agent. */
  ring_timeout?: number;
}

/**
 * The envelope `message` of a refused pickup: `404 not_waiting`, `409
 * being_offered` or `supervisor_busy`, `500 not_started`, `503 no_answer`.
 * A bad device or user is a `400` on `device_id` / `user_id` instead, and
 * anybody but an account administrator a `403`.
 */
export type PickupRefusal = "not_waiting" | "being_offered" | "supervisor_busy" | "not_started" | "no_answer";

/** "First Last", else the username, else the id: how a tier row is labelled. */
export const tierName = (t: Pick<QueueTierRow, "first_name" | "last_name" | "username" | "id">): string =>
  `${t.first_name ?? ""} ${t.last_name ?? ""}`.trim() || t.username || t.id;
